import { baseUniDriverFactory } from '@wix/wix-ui-test-utils/base-driver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { DataHooks, HelperIds } from './constants';

import { ButtonType } from './types';
import { byDataHook } from '../internal/WixUiCoreComponents/test/unidriver';

import { clickable, focusable } from '../../test/unidriver-mixins';
import { dataHookContains } from '../../testkitUtils/utils';

export const paginationDriverFactory = (base: UniDriver) => {
  const getPaginationElement = async () =>
    (await base.attr('data-hook')) === 'pagination-component'
      ? base
      : base.$(byDataHook('pagination-component'));

  const getPageStrip = () => base.$(byDataHook(DataHooks.pageStrip));
  const getCompact = () => base.$(byDataHook(DataHooks.pageCompact));
  const getNavButton = (name: ButtonType) => base.$(byDataHook(name));
  const getPageInput = () => base.$(byDataHook(DataHooks.pageInput));

  const getPageElements = async () => {
    const renderContainer = base.$(
      byDataHook(DataHooks.pageStripRenderContainer),
    );

    return renderContainer._prop('children');
  };
  const getPageByNumber = (n: number) =>
    base.$(dataHookContains(`${DataHooks.page}-${n}`));

  const getTotalPagesLabel = () => base.$(byDataHook(DataHooks.totalPages));

  const getSlashLabel = () => base.$(byDataHook(DataHooks.slashLabel));

  return {
    ...baseUniDriverFactory(base),
    ...clickable(base),

    /** Check if element is rendered */
    isVisible: async () => base.$(byDataHook(DataHooks.pageStrip)).exists(),

    /** Returns currently selected page */
    getCurrentPage: () => base.$(dataHookContains(DataHooks.currentPage)),

    /** Returns a total amount of pages available */
    getTotalPages: async () =>
      parseInt(await base.$(byDataHook(DataHooks.totalPages)).text(), 10),

    /** Returns the current page and total pages in 'compact' mode  */
    getCompactPages: async () =>
      base
        .$(
          `${dataHookContains(DataHooks.pageCompact)} #${
            HelperIds.pageCompact
          }`,
        )
        .text(),

    /** Clicks on gives page number */
    clickOnPage: async (n: number) => base.$(byDataHook(`page-${n}`)).click(),

    /** Clicks on next button */
    clickNextButton: async () => base.$(byDataHook(DataHooks.next)).click(),

    /** Clicks on previous button */
    clickPreviousButton: async () =>
      base.$(byDataHook(DataHooks.previous)).click(),

    /** Returns the container of page elements when pagination is 'pages' mode*/
    getPageStrip,
    /** Returns displayed page elements */
    getPageElements,
    /** Returns the text content of the displayed pages in "pages" mode */
    getPageLabels: async () => {
      const pageElements = [...(await getPageElements())];
      return pageElements.map((p) => p.textContent);
    },
    /** Returns the page element given page number */
    getPageByNumber,
    /** Returns the element for the navigation button - acceptable values are 'first', 'last', 'previous' or 'next' */
    getNavButton,
    /** Returns the all navigation buttons  */
    getNavButtons: () => ({
      firstButton: getNavButton(ButtonType.First),
      previousButton: getNavButton(ButtonType.Prev),
      nextButton: getNavButton(ButtonType.Next),
      lastButton: getNavButton(ButtonType.Last),
    }),
    /** Returns the node in which total pages are displayed */
    getTotalPagesLabel,
    /** Returns the node in which slash is displayed, only in 'compact' mode */
    getSlashLabel,
    /** Returns the container of page elements when pagination is 'compact' mode*/
    getCompact,
    /** Returns the page input element in "input" mode */
    getPageInput: () => {
      const pageInput = getPageInput();
      return {
        ...pageInput,
        ...focusable(pageInput),
      };
    },
    /** Returns the total amount of pages displayed in "input" mode */
    getTotalPagesField: () => base.$(byDataHook(DataHooks.totalPages)),
    /** Simulates changing the value of the input field in "input" mode */
    changeInput: async (newValue: string) => {
      const input = getPageInput();
      await input.enterValue(newValue);
    },
    /** Simulates committing the input field value in "input" mode */
    commitInput: async () => {
      const input = getPageInput();
      await input.pressKey('Enter');
    },
    /** Checks if the input has an error */
    inputHasError: async () => base.attr('data-error'),
    /** Check if element is in mobile environment */
    isMobile: async () => {
      return (await getPaginationElement()).attr('data-mobile');
    },
  };
};

export type PaginationDriver = ReturnType<typeof paginationDriverFactory>;
